<template>
  <div :class="containerClass">
    <page-header title="View Course" :container-class="null" :show-back="true" class="mb-3" />

    <!-- Professional Read-Only Badge -->
    <div class="alert alert-light border-left-primary mb-4" role="alert">
      <div class="d-flex align-items-center">
        <i class="material-icons text-primary mr-2">lock</i>
        <div>
          <strong>Transition Course</strong>
          <small class="d-block text-muted">This is a read-only course managed by the system administrator</small>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="text-center my-5">
      <b-spinner class="align-middle mr-2"></b-spinner>
      <strong>Loading course details...</strong>
    </div>

    <div v-else>
      <!-- Course Overview Card -->
      <b-card class="mb-4 shadow-sm">
        <h5 class="card-title mb-3 text-dark font-weight-bold">Course Overview</h5>

        <div class="row">
          <div class="col-md-3 mb-3">
            <div class="course-image-container">
              <img v-if="course.image_url" :src="course.image_url" class="img-fluid rounded" alt="Course Image" />
              <div v-else class="course-placeholder">
                <i class="material-icons">school</i>
              </div>
            </div>
          </div>

          <div class="col-md-9">
            <div class="mb-3">
              <label class="text-muted small font-weight-bold mb-1">COURSE TITLE</label>
              <h4 class="mb-0">{{ course.title }}</h4>
            </div>

            <div class="mb-3">
              <label class="text-muted small font-weight-bold mb-1">PROGRAM CATEGORY</label>
              <p class="mb-0">{{ course.program_category ? course.program_category.title : '-' }}</p>
            </div>

            <div class="mb-3">
              <label class="text-muted small font-weight-bold mb-1">DESCRIPTION</label>
              <p class="mb-0">{{ course.short_intro }}</p>
            </div>

            <div class="d-flex gap-3">
              <div class="status-badge">
                <i class="material-icons text-success">{{ course.is_published ? 'check_circle' : 'cancel' }}</i>
                <span class="ml-1">{{ course.is_published ? 'Published' : 'Unpublished' }}</span>
              </div>
              <div class="status-badge" v-if="course.passing_percentage">
                <i class="material-icons text-primary">emoji_events</i>
                <span class="ml-1">{{ course.passing_percentage }}% to pass</span>
              </div>
            </div>
          </div>
        </div>
      </b-card>

      <!-- Course Details -->
      <b-card class="mb-4 shadow-sm" v-if="course.description">
        <h5 class="card-title mb-3 text-dark font-weight-bold">Full Description</h5>
        <div class="course-description" v-html="course.description"></div>
      </b-card>

      <!-- Intro Video -->
      <b-card class="mb-4 shadow-sm" v-if="course.preview_video_url">
        <h5 class="card-title mb-3 text-dark font-weight-bold">Introduction Video</h5>
        <video controls class="w-100 rounded" style="max-height: 500px;" :src="course.preview_video_url"></video>
      </b-card>

      <!-- Course Settings -->
      <b-card class="mb-4 shadow-sm">
        <h5 class="card-title mb-3 text-dark font-weight-bold">Course Settings</h5>
        <div class="row">
          <div class="col-md-6 mb-2">
            <div class="setting-item">
              <i class="material-icons">{{ course.restrict_next_lesson ? 'lock' : 'lock_open' }}</i>
              <span
                >Restrict Next Lesson: <strong>{{ course.restrict_next_lesson ? 'Yes' : 'No' }}</strong></span
              >
            </div>
          </div>
          <div class="col-md-6 mb-2">
            <div class="setting-item">
              <i class="material-icons">{{ course.restriction_on_start_date ? 'event' : 'event_available' }}</i>
              <span
                >Restriction on Start Date: <strong>{{ course.restriction_on_start_date ? 'Yes' : 'No' }}</strong></span
              >
            </div>
          </div>
          <div class="col-md-6 mb-2" v-if="course.exam">
            <div class="setting-item">
              <i class="material-icons">assignment</i>
              <span
                >Final Exam: <strong>{{ course.exam.title }}</strong></span
              >
            </div>
          </div>
        </div>
      </b-card>

      <!-- Chapters Section -->
      <div class="section-header mb-3 d-flex align-items-center justify-content-between flex-wrap">
        <h4 class="mb-0">
          <i class="material-icons align-middle mr-2">menu_book</i>
          Course Content
          <span class="badge badge-secondary ml-2">{{ course.total_chapters || 0 }} Chapters</span>
        </h4>

        <b-button
          v-if="course.chapters && course.chapters.length > 0"
          size="sm"
          variant="outline-secondary"
          @click="allExpanded ? collapseAll() : expandAll()"
        >
          <i class="material-icons align-middle">{{ allExpanded ? 'unfold_less' : 'unfold_more' }}</i>
          {{ allExpanded ? 'Collapse All' : 'Expand All' }}
        </b-button>
      </div>

      <div v-if="course.chapters && course.chapters.length > 0" class="chapters-container">
        <b-card
          v-for="(chapter, chapterIndex) in course.chapters"
          :key="chapter.id"
          class="chapter-card mb-3 shadow-sm"
          no-body
        >
          <div
            class="chapter-toggle"
            role="button"
            :aria-expanded="isChapterOpen(chapter.id) ? 'true' : 'false'"
            @click="toggleChapter(chapter.id)"
          >
            <span class="chapter-number">{{ chapterIndex + 1 }}</span>
            <div class="flex-grow-1 ml-3">
              <h5 class="mb-1">{{ chapter.title }}</h5>
              <div class="chapter-meta">
                <span class="meta-item">
                  <i class="material-icons">school</i>
                  {{ chapter.total_lessons || 0 }} lessons
                </span>
                <span class="meta-item">
                  <i class="material-icons">schedule</i>
                  {{ chapter.estimated_time_minutes }} min
                </span>
                <span v-if="chapter.quiz" class="meta-item">
                  <i class="material-icons">quiz</i>
                  Quiz
                </span>
              </div>
            </div>
            <i class="material-icons chapter-chevron" :class="{ open: isChapterOpen(chapter.id) }">expand_more</i>
          </div>

          <b-collapse :visible="isChapterOpen(chapter.id)">
            <div class="chapter-body">
              <p v-if="chapter.short_description" class="text-muted mb-3">{{ chapter.short_description }}</p>

              <div class="chapter-meta mb-3">
                <span class="meta-item">
                  <i class="material-icons">book</i>
                  Page {{ chapter.textbook_start_page }}
                </span>
                <span class="meta-item">
                  <i class="material-icons">event</i>
                  Due in {{ chapter.due_in_days }} days
                </span>
              </div>
              <div
                v-if="chapter.long_description"
                class="chapter-description mt-3 p-3 bg-light rounded"
                v-html="chapter.long_description"
              ></div>

              <div v-if="chapter.image_url" class="mt-3">
                <img
                  :src="chapter.image_url"
                  alt="Chapter Image"
                  class="img-fluid rounded"
                  style="max-height: 300px; object-fit: contain;"
                />
              </div>

              <div v-if="chapter.content_video" class="mt-3">
                <video controls :src="chapter.content_video" class="w-100 rounded" style="max-height: 350px;"></video>
              </div>

              <!-- Lessons -->
              <div v-if="chapter.lessons && chapter.lessons.length > 0" class="lessons-section mt-4">
                <h6 class="section-subtitle">
                  <i class="material-icons">school</i>
                  Lessons ({{ chapter.total_lessons }})
                </h6>

                <div class="lessons-list">
                  <div v-for="(lesson, lessonIndex) in chapter.lessons" :key="lesson.id" class="lesson-item">
                    <div class="lesson-header">
                      <span class="lesson-number">{{ lessonIndex + 1 }}</span>
                      <div class="flex-grow-1 ml-3">
                        <h6 class="mb-1">{{ lesson.title }}</h6>
                        <div class="lesson-meta">
                          <span class="meta-tag">
                            <i class="material-icons">schedule</i>
                            {{ lesson.estimated_time_minutes }} min
                          </span>
                          <span v-if="lesson.quiz" class="meta-tag quiz-tag">
                            <i class="material-icons">quiz</i>
                            Quiz
                          </span>
                          <span v-if="lesson.assignment" class="meta-tag assignment-tag">
                            <i class="material-icons">assignment</i>
                            Assignment
                          </span>
                        </div>
                      </div>
                    </div>

                    <div v-if="lesson.description" class="lesson-description mt-2" v-html="lesson.description"></div>

                    <div v-if="lesson.content_video" class="mt-3">
                      <video controls :src="lesson.content_video" class="w-100 rounded" style="max-height: 300px;"></video>
                    </div>

                    <div v-if="lesson.content_pdf_url" class="mt-2">
                      <a :href="lesson.content_pdf_url" target="_blank" class="btn btn-sm btn-outline-secondary">
                        <i class="material-icons align-middle">picture_as_pdf</i>
                        View PDF Resource
                      </a>
                    </div>

                    <!-- Lesson Quiz -->
                    <div v-if="lesson.quiz" class="quiz-details mt-3">
                      <div class="detail-header">
                        <i class="material-icons">quiz</i>
                        <strong>{{ lesson.quiz.title }}</strong>
                      </div>
                      <div class="detail-grid">
                        <div class="detail-item">
                          <span class="label">Max Attempts</span>
                          <span class="value">{{ lesson.quiz.max_attempts }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="label">Duration</span>
                          <span class="value">{{ lesson.quiz.duration_minutes }} min</span>
                        </div>
                        <div class="detail-item">
                          <span class="label">Total Marks</span>
                          <span class="value">{{ lesson.quiz.total_marks }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="label">Passing %</span>
                          <span class="value">{{ lesson.quiz.passing_percentage }}%</span>
                        </div>
                        <div class="detail-item">
                          <span class="label">Questions</span>
                          <span class="value">{{ lesson.quiz.total_questions }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="label">Weightage</span>
                          <span class="value">{{ lesson.quiz.weightage }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Lesson Assignment -->
                    <div v-if="lesson.assignment" class="assignment-details mt-3">
                      <div class="detail-header">
                        <i class="material-icons">assignment</i>
                        <strong>{{ lesson.assignment.title }}</strong>
                      </div>
                      <p class="text-muted mb-2">{{ lesson.assignment.description }}</p>
                      <div class="detail-grid mb-3">
                        <div class="detail-item">
                          <span class="label">Estimated Time</span>
                          <span class="value">{{ lesson.assignment.estimated_time_minutes }} min</span>
                        </div>
                        <div class="detail-item">
                          <span class="label">Total Tasks</span>
                          <span class="value">{{ lesson.assignment.total_tasks }}</span>
                        </div>
                      </div>

                      <div v-if="lesson.assignment.tasks && lesson.assignment.tasks.length > 0">
                        <strong class="d-block mb-2">Tasks:</strong>
                        <div v-for="(task, taskIndex) in lesson.assignment.tasks" :key="task.id" class="task-item">
                          <div class="d-flex align-items-start">
                            <span class="task-number">{{ taskIndex + 1 }}</span>
                            <div class="flex-grow-1 ml-2">
                              <strong class="d-block">{{ task.title }}</strong>
                              <div class="text-muted small mb-1" v-html="task.description"></div>
                              <span class="badge badge-light">{{ task.completion_mode }}</span>
                              <div
                                v-if="task.case_study_content"
                                class="mt-2 p-2 bg-white rounded border"
                                v-html="task.case_study_content"
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Chapter Quiz -->
              <div v-if="chapter.quiz" class="chapter-quiz mt-4">
                <h6 class="section-subtitle">
                  <i class="material-icons">quiz</i>
                  Chapter Quiz: {{ chapter.quiz.title }}
                </h6>

                <div class="detail-grid mb-3">
                  <div class="detail-item">
                    <span class="label">Max Attempts</span>
                    <span class="value">{{ chapter.quiz.max_attempts }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Duration</span>
                    <span class="value">{{ chapter.quiz.duration_minutes }} min</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Total Marks</span>
                    <span class="value">{{ chapter.quiz.total_marks }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Passing %</span>
                    <span class="value">{{ chapter.quiz.passing_percentage }}%</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Questions</span>
                    <span class="value">{{ chapter.quiz.total_questions }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Weightage</span>
                    <span class="value">{{ chapter.quiz.weightage }}</span>
                  </div>
                </div>

                <div v-if="chapter.quiz.questions && chapter.quiz.questions.length > 0">
                  <strong class="d-block mb-3">Questions:</strong>
                  <div v-for="(question, qIndex) in chapter.quiz.questions" :key="question.id" class="question-item mb-3">
                    <div class="d-flex align-items-start">
                      <span class="question-number">Q{{ qIndex + 1 }}</span>
                      <div class="flex-grow-1 ml-3">
                        <p class="mb-2">{{ question.question_text }}</p>
                        <small class="text-muted">Marks: {{ question.marks }} | Type: {{ question.type }}</small>

                        <div v-if="question.image_url" class="mt-2">
                          <img
                            :src="question.image_url"
                            alt="Question"
                            class="img-fluid rounded"
                            style="max-height: 200px;"
                          />
                        </div>

                        <div v-if="question.options && question.options.length > 0" class="options-list mt-2">
                          <div
                            v-for="option in question.options"
                            :key="option.id"
                            class="option-item"
                            :class="{ 'correct-option': option.is_correct }"
                          >
                            {{ option.option_text }}
                            <i v-if="option.is_correct" class="material-icons text-success">check_circle</i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-collapse>
        </b-card>
      </div>

      <div v-else class="empty-state">
        <i class="material-icons">folder_open</i>
        <p>No chapters available for this course</p>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import { mapActions } from 'vuex';
import Page from '../../../components/Page.vue';

export default {
  components: {
    PageHeader,
  },
  extends: Page,

  data() {
    return {
      title: 'View Course',
      isLoading: true,
      course: {
        title: '',
        image_url: '',
        description: '',
        short_intro: '',
        program_category: null,
        preview_video_url: '',
        is_published: false,
        passing_percentage: 0,
        exam: null,
        restrict_next_lesson: false,
        restriction_on_start_date: false,
        chapters: [],
        total_chapters: 0,
      },
      expandedChapters: [],
      windowWidth: window.innerWidth,
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Courses', to: { name: 'list-courses' } },
        {
          text: 'View Course',
          active: true,
        },
      ];
    },
    isMobileScreen() {
      return this.windowWidth <= 420;
    },
    allExpanded() {
      const chapters = this.course.chapters || [];
      return chapters.length > 0 && this.expandedChapters.length === chapters.length;
    },
  },

  methods: {
    ...mapActions('courses', ['getSuperCourseDetail']),

    async fetchCourseDetails() {
      this.isLoading = true;
      try {
        const course_id = this.$route.params.id;
        const response = await this.getSuperCourseDetail({ course_id: course_id });

        if (response.data) {
          this.course = response.data;
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load course details' });
        this.$router.push({ name: 'list-courses' });
      }
      this.isLoading = false;
    },

    isChapterOpen(chapterId) {
      return this.expandedChapters.includes(chapterId);
    },

    toggleChapter(chapterId) {
      if (this.isChapterOpen(chapterId)) {
        this.expandedChapters = this.expandedChapters.filter((id) => id !== chapterId);
      } else {
        this.expandedChapters = [...this.expandedChapters, chapterId];
      }
    },

    expandAll() {
      this.expandedChapters = (this.course.chapters || []).map((chapter) => chapter.id);
    },

    collapseAll() {
      this.expandedChapters = [];
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },

  async mounted() {
    await this.fetchCourseDetails();
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped lang="scss">
// Professional Color Palette
$primary-color: #2c3e50;
$secondary-color: #34495e;
$accent-color: #3498db;
$success-color: #27ae60;
$border-color: #e1e8ed;
$bg-light: #f8f9fa;
$text-muted: #6c757d;

.border-left-primary {
  border-left: 4px solid $accent-color !important;
}

.course-image-container {
  width: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid $border-color;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.course-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $bg-light;

  i {
    font-size: 48px;
    color: $text-muted;
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: $bg-light;
  border-radius: 20px;
  font-size: 0.875rem;
  color: $secondary-color;

  i {
    font-size: 18px;
  }
}

.course-description {
  color: $secondary-color;
  line-height: 1.6;
}

.setting-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  color: $secondary-color;

  i {
    font-size: 20px;
    color: $text-muted;
  }
}

.section-header {
  padding: 16px 0;
  border-bottom: 2px solid $border-color;

  h4 {
    color: $primary-color;
    font-weight: 600;
  }
}

.chapters-container {
  margin-top: 24px;
}

.chapter-card {
  border: 1px solid $border-color;
  border-radius: 8px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  }
}

.chapter-toggle {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: $bg-light;
  }

  h5 {
    color: $primary-color;
    font-weight: 600;
  }
}

.chapter-chevron {
  color: $text-muted;
  transition: transform 0.2s;

  &.open {
    transform: rotate(180deg);
  }
}

.chapter-body {
  padding: 0 20px 20px;
  border-top: 1px solid $border-color;
  padding-top: 16px;
}

.chapter-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: $accent-color;
  color: white;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.125rem;
}

.chapter-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  .meta-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: $text-muted;
    font-size: 0.875rem;

    i {
      font-size: 18px;
    }
  }
}

.chapter-description {
  background: $bg-light;
  border-left: 3px solid $border-color;
}

.section-subtitle {
  display: flex;
  align-items: center;
  gap: 8px;
  color: $primary-color;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid $border-color;

  i {
    color: $accent-color;
  }
}

.lessons-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.lesson-item {
  padding: 16px;
  background: $bg-light;
  border-radius: 8px;
  border: 1px solid $border-color;
}

.lesson-header {
  display: flex;
  align-items: flex-start;
}

.lesson-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: white;
  border: 2px solid $accent-color;
  color: $accent-color;
  border-radius: 50%;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.lesson-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.meta-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: white;
  border: 1px solid $border-color;
  border-radius: 12px;
  font-size: 0.75rem;
  color: $secondary-color;

  i {
    font-size: 14px;
  }
}

.quiz-tag {
  border-color: #3498db;
  color: #3498db;
  background: #ebf5fb;
}

.assignment-tag {
  border-color: #e67e22;
  color: #e67e22;
  background: #fef5e7;
}

.lesson-description {
  color: $secondary-color;
  font-size: 0.875rem;
  padding-left: 44px;
}

.quiz-details,
.assignment-details {
  padding: 16px;
  background: white;
  border: 1px solid #d6eaf8;
  border-radius: 8px;
  margin-left: 44px;
}

.assignment-details {
  border-color: #fdebd0;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: $primary-color;

  i {
    color: $accent-color;
  }
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;

  .label {
    font-size: 0.75rem;
    color: $text-muted;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }

  .value {
    font-weight: 600;
    color: $secondary-color;
  }
}

.task-item {
  padding: 12px;
  background: $bg-light;
  border-radius: 6px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.task-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: $accent-color;
  color: white;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.chapter-quiz {
  padding: 20px;
  background: #f8fcff;
  border: 1px solid #d6eaf8;
  border-radius: 8px;
}

.question-item {
  padding: 16px;
  background: white;
  border: 1px solid $border-color;
  border-radius: 8px;
}

.question-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  background: $accent-color;
  color: white;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-item {
  padding: 10px 14px;
  background: $bg-light;
  border: 1px solid $border-color;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &.correct-option {
    background: #d5f4e6;
    border-color: $success-color;
    color: $success-color;
    font-weight: 500;
  }

  i {
    font-size: 20px;
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: $text-muted;

  i {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  p {
    font-size: 1.125rem;
  }
}

.shadow-sm {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
}

.gap-3 {
  gap: 1rem;
}
</style>
